import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  NotificationAPIResponse,
  NotificationType,
  NotificationFiltersResponse,
  NotificationBadgeResponse,
} from 'src/components/notifications/types';
import * as reduxApi from 'src/utils/redux-api-v2';
import {camel} from 'src/utils';
import logger from 'src/utils/logger';


type NotificationAPIResponseWithAppendData = NotificationAPIResponse & {
  appendData: boolean;
};

export type NotificationsActions =
  | ReceiveNotificationsAction
  | SetBadgeAction
  | AddNewUnreadNotificationAction
  | NudgeNotificationReadAction
  | RemoveNotificationFromListAction
  | ReceiveNotificationFiltersAction;

export const SET_BADGE = 'notifications/setBadge';
export const RECEIVE_NOTIFICATIONS = 'notifications/receiveNotifications';
export const ADD_NEW_UNREAD_NOTIFICATION =
  'notifications/addNewUnreadNotification';
export const NUDGE_NOTIFICATION_READ = 'notifications/nudgeNotificationRead';
export const REMOVE_NOTIFICATION_FROM_LIST =
  'notifications/removeNotificationFromList';
export const RECEIVE_NOTIFICATION_FILTERS =
  'notifications/receiveNotificationFilters';

export type ReceiveNotificationFiltersAction = {
  type: typeof RECEIVE_NOTIFICATION_FILTERS;
  payload: string[];
};

export const receiveNotificationFilters = (
  payload: string[],
): ReceiveNotificationFiltersAction => ({
  type: RECEIVE_NOTIFICATION_FILTERS,
  payload,
});

export type NudgeNotificationReadAction = {
  type: typeof NUDGE_NOTIFICATION_READ;
  payload: {notificationId: string; readAt: string};
};

export const nudgeNotificationRead = (
  notificationId: string,
  readAt: string,
): NudgeNotificationReadAction => ({
  type: NUDGE_NOTIFICATION_READ,
  payload: {notificationId, readAt},
});

export type RemoveNotificationFromListAction = {
  type: typeof REMOVE_NOTIFICATION_FROM_LIST;
  payload: string;
};

export const removeNotificationFromList = (
  notificationId: string,
): RemoveNotificationFromListAction => ({
  type: REMOVE_NOTIFICATION_FROM_LIST,
  payload: notificationId,
});

export type AddNewUnreadNotificationAction = {
  type: typeof ADD_NEW_UNREAD_NOTIFICATION;
  payload: NotificationType;
};

export const addNewUnreadNotification = (
  notification: NotificationType,
): AddNewUnreadNotificationAction => ({
  type: ADD_NEW_UNREAD_NOTIFICATION,
  payload: notification,
});
export type SetBadgeAction = {
  type: typeof SET_BADGE;
  payload: boolean;
};

export const setBadge = (payload: boolean): SetBadgeAction => ({
  type: SET_BADGE,
  payload,
});

export type ReceiveNotificationsAction = {
  type: typeof RECEIVE_NOTIFICATIONS;
  payload: NotificationAPIResponseWithAppendData;
};

export const receiveNotifications = (
  payload: NotificationAPIResponseWithAppendData,
): ReceiveNotificationsAction => ({
  type: RECEIVE_NOTIFICATIONS,
  payload,
});

export const getBadgeStatus = () => async (dispatch: Dispatch) => {
  try {
    const response: NotificationBadgeResponse = await dispatch(
      reduxApi.get('messages_v2/feed/badge'),
    );
    const badgeStatus = response?.show_badge || false;
    dispatch(setBadge(badgeStatus));
  } catch (error) {
    logger.error('Failed to get badge status', error);
  }
};

export const markBadgeAsRead = () => async (dispatch: Dispatch) => {
  try {
    await dispatch(reduxApi.post('messages_v2/feed/seen'));
    dispatch(setBadge(false));
  } catch (error) {
    logger.error('Failed to mark badge as read', error);
  }
};

export const markNotificationAsRead =
  (notificationId: string, removeNotification: Boolean) =>
  async (dispatch: Dispatch) => {
    try {
      await dispatch(
        reduxApi.patch(`messages_v2/feed/nudges/${notificationId}/read`),
      );
      if (removeNotification) {
        dispatch(removeNotificationFromList(notificationId));
      }
    } catch (error) {
      logger.error('Failed to mark notification as read', error);
    }
  };

export const getNotifications =
  ({
    appendData,
    source = '',
    cursor = '',
    view = '',
  }: {
    appendData: boolean;
    source: string;
    view: string;
    cursor?: string;
  }): ThunkAction<void> =>
  async (dispatch: Dispatch) => {
    const limit = 10;
    try {
      let url = `messages_v2/feed/nudges?limit=${limit}`;
      if (view) {
        url += `&view=${view}`;
      }
      if (source) {
        url += `&source=${source}`;
      }
      if (cursor) {
        url += `&cursor=${cursor}`;
      }
      const response: NotificationAPIResponse = camel(
        await dispatch(reduxApi.get(url)),
      );

      if (response) {
        dispatch(
          receiveNotifications({
            items: response.items,
            nextCursor: response.nextCursor,
            hasMore: response.hasMore,
            appendData,
          }),
        );
      }
    } catch (error) {
      logger.error('Failed to get notifications', error);
    }
  };

export const getNotificationFilters = () => async (dispatch: Dispatch) => {
  try {
    const response: NotificationFiltersResponse = camel(
      await dispatch(reduxApi.get('messages_v2/feed/filters')),
    );
    if (response) {
      dispatch(receiveNotificationFilters(response.sources));
    }
  } catch (error) {
    logger.error('Failed to get notification filters', error);
  }
};
